import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { downloadCSV, CURRENCIES, CLIENT_TYPES, MONTHS } from '../lib/utils';
import { Download, FileSpreadsheet, Users, FileText, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ExportPage() {
  const [invoiceFilters, setInvoiceFilters] = useState({
    financial_year: '',
    billing_month: '',
    invoice_status: '',
    bill_currency: '',
    client_type: ''
  });
  const [clientFilters, setClientFilters] = useState({
    active: '',
    type: ''
  });
  const [exporting, setExporting] = useState('');
  const [financialYears, setFinancialYears] = useState([]);
  const [loadedFY, setLoadedFY] = useState(false);

  const loadFinancialYears = async () => {
    if (loadedFY) return;
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setFinancialYears(response.data.financial_years || []);
      setLoadedFY(true);
    } catch (error) {
      console.error('Failed to load FY:', error);
    }
  };

  const exportInvoices = async () => {
    setExporting('invoices');
    try {
      const params = {};
      if (invoiceFilters.financial_year) params.financial_year = invoiceFilters.financial_year;
      if (invoiceFilters.billing_month) params.billing_month = invoiceFilters.billing_month;
      if (invoiceFilters.invoice_status) params.invoice_status = invoiceFilters.invoice_status;
      if (invoiceFilters.bill_currency) params.bill_currency = invoiceFilters.bill_currency;
      if (invoiceFilters.client_type) params.client_type = invoiceFilters.client_type;

      const response = await axios.get(`${API_URL}/export/invoices`, { params });
      
      if (response.data.length === 0) {
        toast.error('No data to export with selected filters');
        return;
      }

      downloadCSV(response.data, 'invoices_export');
      toast.success(`Exported ${response.data.length} invoices`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  const exportClients = async () => {
    setExporting('clients');
    try {
      const params = {};
      if (clientFilters.active !== '') params.active = clientFilters.active === 'true';
      if (clientFilters.type) params.type = clientFilters.type;

      const response = await axios.get(`${API_URL}/export/clients`, { params });
      
      if (response.data.length === 0) {
        toast.error('No data to export with selected filters');
        return;
      }

      downloadCSV(response.data, 'clients_export');
      toast.success(`Exported ${response.data.length} clients`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="export-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Export Center</h1>
        <p className="text-muted-foreground">Export your data to CSV files</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Invoices */}
        <Card className="hover-lift">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/10">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Export Invoices</CardTitle>
                <CardDescription>Download invoice data as CSV</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Financial Year</label>
                <Select 
                  value={invoiceFilters.financial_year || "all"} 
                  onValueChange={(v) => setInvoiceFilters({...invoiceFilters, financial_year: v === "all" ? "" : v})}
                  onOpenChange={loadFinancialYears}
                >
                  <SelectTrigger data-testid="export-invoice-fy">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {financialYears.map(fy => (
                      <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Billing Month</label>
                <Select 
                  value={invoiceFilters.billing_month || "all"} 
                  onValueChange={(v) => setInvoiceFilters({...invoiceFilters, billing_month: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-invoice-month">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {MONTHS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select 
                  value={invoiceFilters.invoice_status || "all"} 
                  onValueChange={(v) => setInvoiceFilters({...invoiceFilters, invoice_status: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-invoice-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Currency</label>
                <Select 
                  value={invoiceFilters.bill_currency || "all"} 
                  onValueChange={(v) => setInvoiceFilters({...invoiceFilters, bill_currency: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-invoice-currency">
                    <SelectValue placeholder="All Currencies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Currencies</SelectItem>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Client Type</label>
                <Select 
                  value={invoiceFilters.client_type || "all"} 
                  onValueChange={(v) => setInvoiceFilters({...invoiceFilters, client_type: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-invoice-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {CLIENT_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              className="w-full" 
              onClick={exportInvoices}
              disabled={exporting === 'invoices'}
              data-testid="export-invoices-btn"
            >
              {exporting === 'invoices' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Invoices
            </Button>
          </CardContent>
        </Card>

        {/* Export Clients */}
        <Card className="hover-lift">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-secondary/10">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <CardTitle>Export Clients</CardTitle>
                <CardDescription>Download client data as CSV</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select 
                  value={clientFilters.active || "all"} 
                  onValueChange={(v) => setClientFilters({...clientFilters, active: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-client-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Type</label>
                <Select 
                  value={clientFilters.type || "all"} 
                  onValueChange={(v) => setClientFilters({...clientFilters, type: v === "all" ? "" : v})}
                >
                  <SelectTrigger data-testid="export-client-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {CLIENT_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              className="w-full" 
              onClick={exportClients}
              disabled={exporting === 'clients'}
              data-testid="export-clients-btn"
            >
              {exporting === 'clients' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Clients
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* CSV Format Guide */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-accent/20">
              <FileSpreadsheet className="h-6 w-6 text-accent-foreground" />
            </div>
            <div>
              <CardTitle>CSV Import Format Guide</CardTitle>
              <CardDescription>Use these column headers when importing data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-primary">Invoices CSV Columns</h4>
              <ul className="space-y-2 text-sm">
                {[
                  { col: 'client_name*', desc: 'Client name (must exist)' },
                  { col: 'billing_month*', desc: 'Month name (January, etc.)' },
                  { col: 'billing_year*', desc: 'Year (2024, etc.)' },
                  { col: 'payment_date', desc: 'YYYY-MM-DD format' },
                  { col: 'paid_amount_inr', desc: 'Amount received in INR' },
                  { col: 'bill_amount*', desc: 'Invoice amount' },
                  { col: 'bill_currency*', desc: 'Currency code' },
                  { col: 'notes', desc: 'Optional notes' }
                ].map((item) => (
                  <li key={item.col} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                    <code className="bg-muted px-2 py-0.5 rounded text-xs font-mono">{item.col}</code>
                    <span className="text-xs text-muted-foreground">{item.desc}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-primary/5 rounded-lg">
                <h5 className="font-medium text-sm mb-2">Export includes additional fields:</h5>
                <div className="flex flex-wrap gap-1">
                  {['invoice_no', 'client_type', 'billing_financial_year', 'exchange_rate', 'estimated_revenue_inr', 'financial_year', 'invoice_status', 'payment_method', 'receiving_bank'].map(field => (
                    <code key={field} className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{field}</code>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3 text-secondary">Clients CSV Columns</h4>
              <ul className="space-y-2 text-sm">
                {[
                  { col: 'client_name*', desc: 'Company/Client name' },
                  { col: 'active', desc: 'true/false' },
                  { col: 'type*', desc: 'Client or Partner' },
                  { col: 'contract_amount', desc: 'Contract value' },
                  { col: 'contract_currency', desc: 'Currency code' },
                  { col: 'payment_method*', desc: 'WISE, PayPal, etc.' },
                  { col: 'receiving_bank*', desc: 'SBI, HDFC, Other' },
                  { col: 'client_representative', desc: 'Contact person name' },
                  { col: 'representative_email', desc: 'Contact email' },
                  { col: 'representative_phone', desc: 'Contact phone' },
                  { col: 'notes', desc: 'Optional notes' }
                ].map((item) => (
                  <li key={item.col} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0" />
                    <code className="bg-muted px-2 py-0.5 rounded text-xs font-mono">{item.col}</code>
                    <span className="text-xs text-muted-foreground">{item.desc}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 p-3 bg-secondary/5 rounded-lg">
                <h5 className="font-medium text-sm mb-2">Export includes additional fields:</h5>
                <div className="flex flex-wrap gap-1">
                  {['id', 'total_revenue_inr', 'created_at', 'updated_at'].map(field => (
                    <code key={field} className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{field}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold mb-2">Valid Values Reference</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">Type:</span>
                <span className="text-muted-foreground ml-2">Client, Partner</span>
              </div>
              <div>
                <span className="font-medium">Currency:</span>
                <span className="text-muted-foreground ml-2">INR, USD, GBP, EUR, AUD, SGD, CAD, JPY, AED, CHF, NZD, HKD, Other</span>
              </div>
              <div>
                <span className="font-medium">Payment Method:</span>
                <span className="text-muted-foreground ml-2">WISE, PayPal, Payoneer, Bank Transfer</span>
              </div>
              <div>
                <span className="font-medium">Receiving Bank:</span>
                <span className="text-muted-foreground ml-2">SBI, HDFC, Other</span>
              </div>
              <div>
                <span className="font-medium">Billing Month:</span>
                <span className="text-muted-foreground ml-2">January, February, ... December</span>
              </div>
              <div>
                <span className="font-medium">Date Format:</span>
                <span className="text-muted-foreground ml-2">YYYY-MM-DD (e.g., 2024-12-15)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
